import { useCallback, useRef } from "react";

/**
 * Last-one-wins guard for overlapping async reads into the same piece of state.
 *
 * TECH_DEBT #46. Three places did this:
 *
 *     setSelectedId(id);
 *     const detail = await fetchDetail(id);
 *     setSelectedDetail(detail);        // ← whichever response lands LAST wins
 *
 * Responses do not arrive in the order they were asked for. Click row A then row
 * B and A's slower reply lands second, so the list highlights B while the pane
 * shows A — and on `AccountsPage` the same shape left a payment modal showing
 * one supplier's invoices under another supplier's name, with the allocation
 * checkboxes keyed to those invoice ids. That is a money-entry screen, so it is
 * not a cosmetic race.
 *
 * Usage — stamp before the await, check after it:
 *
 *     const beginRequest = useLatestRequest();
 *     ...
 *     const isCurrent = beginRequest();
 *     const detail = await fetchDetail(id);
 *     if (!isCurrent()) return;          // a newer request has since started
 *     setSelectedDetail(detail);
 *
 * Check the loading flag too, not just the data: a superseded response clearing
 * `loading` turns off the spinner for a request that is still running.
 *
 * Deliberately not an AbortController. Aborting cancels the HTTP request, which
 * is a different and stronger thing — these callers share responses through a
 * cache and an abort would throw inside `Promise.all` siblings that are still
 * wanted. This only decides whose answer is allowed to reach `setState`.
 *
 * One counter per hook instance, so a component with two independent races
 * (a detail pane and a modal, say) calls it twice rather than sharing one.
 */
export function useLatestRequest(): () => () => boolean {
  const latest = useRef(0);

  return useCallback(() => {
    const id = ++latest.current;
    return () => id === latest.current;
  }, []);
}
